import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export type StatementType = 'restaurant' | 'partner';

export interface StatementOrder {
  orderNo: string;
  date: string;
  entity: string;        // restaurant name or partner name
  status: string;
  productAmount: number; // totalPrice
  deliveryCharge: number;
  packing: number;
  surge: number;
  platformFee: number;
  collected: number;     // customerPaid
}

// All selectable columns. `restaurantOnly`/`partnerOnly` hint defaults per type.
export const STATEMENT_COLUMNS: { key: string; label: string; numeric: boolean }[] = [
  { key: 'orderNo', label: 'Order #', numeric: false },
  { key: 'date', label: 'Date', numeric: false },
  { key: 'entity', label: 'Name', numeric: false },
  { key: 'status', label: 'Status', numeric: false },
  { key: 'productAmount', label: 'Product ₹', numeric: true },
  { key: 'deliveryCharge', label: 'Delivery ₹', numeric: true },
  { key: 'packing', label: 'Packing ₹', numeric: true },
  { key: 'surge', label: 'Surge ₹', numeric: true },
  { key: 'platformFee', label: 'Platform ₹', numeric: true },
  { key: 'collected', label: 'Collected ₹', numeric: true },
  { key: 'commission', label: 'Commission ₹', numeric: true },
  { key: 'net', label: 'Net ₹', numeric: true },
];

export interface StatementOptions {
  type: StatementType;
  heading: string;       // e.g. "Restaurant Statement"
  entityName: string;    // selected restaurant/partner or "All"
  rangeLabel: string;    // e.g. "01 Jun 2026 – 16 Jun 2026"
  statusLabel: string;
  generatedAt: string;
  commissionPct: number;
  columns: string[];     // selected column keys (in display order)
  orders: StatementOrder[];
}

// jsPDF's core Helvetica font has no ₹ glyph (it renders as "¹"), so the PDF uses "Rs".
const money = (n: number) => `Rs ${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// commission/net are computed per row from productAmount + commissionPct
function cellValue(o: StatementOrder, key: string, pct: number): string {
  switch (key) {
    case 'orderNo': return o.orderNo;
    case 'date': return o.date;
    case 'entity': return o.entity;
    case 'status': return o.status;
    case 'productAmount': return money(o.productAmount);
    case 'deliveryCharge': return money(o.deliveryCharge);
    case 'packing': return money(o.packing);
    case 'surge': return money(o.surge);
    case 'platformFee': return money(o.platformFee);
    case 'collected': return money(o.collected);
    case 'commission': return money(o.productAmount * pct / 100);
    case 'net': return money(o.productAmount - o.productAmount * pct / 100);
    default: return '';
  }
}

// Per-entity totals summary (grouped by restaurant / delivery partner).
export function generateSummaryPdf(opts: {
  heading: string;
  periodLabel: string;
  entityLabel: string;
  columns: { label: string }[];
  rows: (string | number)[][];
  totals: (string | number)[];
}): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(15);
  doc.text(opts.heading, 40, 40);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(opts.periodLabel, 40, 58);
  doc.setTextColor(0);
  autoTable(doc, {
    startY: 74,
    head: [[opts.entityLabel, 'Orders', ...opts.columns.map(c => c.label)]],
    body: opts.rows,
    foot: [opts.totals],
    theme: 'grid',
    headStyles: { fillColor: [29, 78, 216], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    footStyles: { fillColor: [239, 246, 255], textColor: [29, 78, 216], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { halign: 'left' } },
    styles: { halign: 'right', cellPadding: 4 },
    margin: { left: 40, right: 40 },
  });
  const fname = opts.heading.replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  doc.save(`${fname}.pdf`);
}

export function generateStatementPdf(opts: StatementOptions): void {
  const { orders, columns, commissionPct, type } = opts;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const teal: [number, number, number] = [33, 128, 141];

  // ── Header ──
  doc.setFillColor(...teal);
  doc.rect(0, 0, pageW, 56, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('Zapee', 40, 34);
  doc.setFontSize(12); doc.setFont('helvetica', 'normal');
  doc.text(opts.heading, 110, 34);

  // ── Meta block ──
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(9);
  const metaLeft = [
    `Name: ${opts.entityName}`,
    `Period: ${opts.rangeLabel}`,
    `Status: ${opts.statusLabel}`,
  ];
  const metaRight = [
    `Orders: ${orders.length}`,
    type === 'restaurant' ? `Commission: ${commissionPct}%` : `Type: Delivery Partner`,
    `Generated: ${opts.generatedAt}`,
  ];
  metaLeft.forEach((t, i) => doc.text(t, 40, 76 + i * 14));
  metaRight.forEach((t, i) => doc.text(t, pageW - 40 - doc.getTextWidth(t), 76 + i * 14));

  // ── Table ──
  const cols = STATEMENT_COLUMNS.filter(c => columns.includes(c.key));
  const head = [cols.map(c => c.label.replace('₹', 'Rs'))];
  const body = orders.map(o => cols.map(c => cellValue(o, c.key, commissionPct)));

  // Totals row
  const sum = (f: (o: StatementOrder) => number) => orders.reduce((s, o) => s + (f(o) || 0), 0);
  const totalsFor = (key: string): string => {
    switch (key) {
      case 'productAmount': return money(sum(o => o.productAmount));
      case 'deliveryCharge': return money(sum(o => o.deliveryCharge));
      case 'packing': return money(sum(o => o.packing));
      case 'surge': return money(sum(o => o.surge));
      case 'platformFee': return money(sum(o => o.platformFee));
      case 'collected': return money(sum(o => o.collected));
      case 'commission': return money(sum(o => o.productAmount) * commissionPct / 100);
      case 'net': return money(sum(o => o.productAmount) * (1 - commissionPct / 100));
      default: return '';
    }
  };
  const totalRow = cols.map((c, i) => i === 0 ? 'TOTAL' : (c.numeric ? totalsFor(c.key) : ''));

  autoTable(doc, {
    startY: 124,
    head,
    body,
    foot: [totalRow],
    theme: 'striped',
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: teal, textColor: 255, fontStyle: 'bold', halign: 'left' },
    footStyles: { fillColor: [232, 245, 247], textColor: [26, 35, 50], fontStyle: 'bold', halign: 'left' },
    // Left-align everything so amounts sit straight below their header.
    columnStyles: cols.reduce((acc, c, i) => {
      acc[i] = { halign: 'left' };
      return acc;
    }, {} as Record<number, any>),
    margin: { left: 40, right: 40 },
  });

  // ── Summary box (the requested sums) ──
  let y = (doc as any).lastAutoTable.finalY + 24;
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...teal);
  doc.text('Summary', 40, y);
  y += 8;
  doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40); doc.setFontSize(10);

  const summaryLines: [string, string][] = type === 'partner'
    ? [
        ['Total Collected (from customers)', money(sum(o => o.collected))],
        ['Total Delivery Amount', money(sum(o => o.deliveryCharge))],
        ['Total Packing Amount', money(sum(o => o.packing))],
        ['Total Surge Amount', money(sum(o => o.surge))],
        ['Total Platform Amount', money(sum(o => o.platformFee))],
        ['Grand Total (delivery+packing+surge+platform)',
          money(sum(o => o.deliveryCharge) + sum(o => o.packing) + sum(o => o.surge) + sum(o => o.platformFee))],
      ]
    : [
        ['Total Product Amount', money(sum(o => o.productAmount))],
        [`Commission (${commissionPct}%)`, money(sum(o => o.productAmount) * commissionPct / 100)],
        ['Net Payable to Restaurant', money(sum(o => o.productAmount) * (1 - commissionPct / 100))],
        ['Total Delivery Amount', money(sum(o => o.deliveryCharge))],
        ['Total Packing Amount', money(sum(o => o.packing))],
        ['Total Platform Amount', money(sum(o => o.platformFee))],
      ];

  summaryLines.forEach(([label, val], i) => {
    const yy = y + 16 + i * 16;
    doc.text(label, 48, yy);
    doc.text(val, 340 - doc.getTextWidth(val), yy);
  });

  const safe = (s: string) => s.replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
  doc.save(`zapee_${type}_statement_${safe(opts.entityName)}.pdf`);
}
